import { GoogleAuthProvider, OAuthProvider, signInWithPopup } from "firebase/auth";
import { useEffect, useState, useRef } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import Apple from "../assets/apple.png";
import Google from "../assets/google.png";
import { auth } from "../firebase";
import { captureException } from "@sentry/react";
import useAuth from "../hooks/useAuth";
import { trackEvent } from "@/lib/amplitude";

export default function Login() {
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [marketingAccepted, setMarketingAccepted] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const { t, i18n } = useTranslation();
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  const hasTrackedPageView = useRef(false);
  const placeRef = window.localStorage.getItem("placeRef");

  // Track page view
  useEffect(() => {
    if (!hasTrackedPageView.current) {
      hasTrackedPageView.current = true;
      trackEvent("login_page_viewed", {
        placeRef,
        locale: i18n.language,
      });
    }
  }, []);

  // Redirect authenticated users to upload page
  useEffect(() => {
    if (!loading && user) {
      const redirectPath = window.localStorage.getItem("redirectAfterLogin");
      if (redirectPath) {
        try {
          window.localStorage.removeItem("redirectAfterLogin");
          navigate(redirectPath);
        } catch (error) {
          captureException(error);
          console.error("Failed to redirect after login:", error);
          navigate("/upload");
        }
      } else {
        navigate("/upload");
      }
    }
  }, [user, loading, navigate]);

  const handleLogin = async (provider: GoogleAuthProvider | OAuthProvider) => {
    const providerName = provider instanceof GoogleAuthProvider ? "google" : "apple";
    try {
      if (!termsAccepted) {
        alert(t("login.alert-terms"));
        return;
      }
      trackEvent("login_started", {
        provider: providerName,
        placeRef,
        termsAccepted,
        marketingAccepted,
      });
      setIsRedirecting(true);
      await signInWithPopup(auth, provider);
      trackEvent("login_success", {
        provider: providerName,
        placeRef,
      });
      // Navigation will happen via useEffect when user state changes
    } catch (error) {
      console.error(error);
      captureException(error);
      trackEvent("login_error", {
        provider: providerName,
        placeRef,
        error: error instanceof Error ? error.message : "Unknown error",
      });
      setIsRedirecting(false);
      alert(t("login.alert-error"));
    }
  };

  useEffect(() => {
    if (marketingAccepted) {
      window.localStorage.setItem("marketing", "true");
    } else {
      window.localStorage.setItem("marketing", "false");
    }
  }, [marketingAccepted]);

  return (
    <div className="relative min-h-screen w-full bg-primary">
      {/* Main content area */}
      <div className="mx-auto flex min-h-[100dvh] max-w-md flex-col items-center justify-center px-4 pb-96">
        <img
          src="/bestiebite.svg"
          alt="Bestie Bite"
          className="mb-5 w-4/5 object-contain"
        />
      </div>

      {/* White drawer at bottom */}
      <motion.div
        initial={{ y: "100%" }}
        animate={{ y: 0 }}
        transition={{
          type: "spring",
          damping: 30,
          stiffness: 300,
          delay: 0.2,
        }}
        className="fixed bottom-0 left-0 right-0 mx-auto max-w-md rounded-t-3xl bg-white px-6 py-8 shadow-2xl"
      >
        {/* Login buttons */}
        <div className="mb-6 flex flex-col gap-3">
          <button
            onClick={() => handleLogin(new GoogleAuthProvider())}
            disabled={isRedirecting}
            className="flex items-center justify-center gap-3 rounded-xl border border-gray-200 bg-white px-6 py-4 font-semibold text-gray-800 shadow-sm transition-all hover:shadow-md active:scale-95 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isRedirecting ? (
              <div className="h-6 w-6 animate-spin rounded-full border-2 border-gray-300 border-t-gray-800"></div>
            ) : (
              <img
                src={Google}
                width={24}
                height={24}
                className="object-contain"
                alt="Google"
              />
            )}
            Continue with Google
          </button>

          <button
            onClick={() => handleLogin(new OAuthProvider("apple.com"))}
            disabled={isRedirecting}
            className="flex items-center justify-center gap-3 rounded-xl border border-gray-200 bg-white px-6 py-4 font-semibold text-gray-800 shadow-sm transition-all hover:shadow-md active:scale-95 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isRedirecting ? (
              <div className="h-6 w-6 animate-spin rounded-full border-2 border-gray-300 border-t-gray-800"></div>
            ) : (
              <img
                src={Apple}
                width={24}
                height={24}
                className="object-contain"
                alt="Apple"
              />
            )}
            Continue with Apple
          </button>
        </div>

        {/* Terms and marketing checkboxes */}
        <div className="flex flex-col space-y-3 text-sm text-gray-700">
          <label htmlFor="terms" className="flex cursor-pointer items-start space-x-2">
            <input
              type="checkbox"
              id="terms"
              checked={termsAccepted}
              onChange={() => {
                const newValue = !termsAccepted;
                setTermsAccepted(newValue);
                if (newValue) {
                  trackEvent("terms_accepted", { placeRef });
                }
              }}
              className="mt-0.5 size-4 cursor-pointer rounded border-gray-300 text-primary accent-primary focus:ring-2 focus:ring-primary"
            />
            <span className="flex-1 text-xs leading-relaxed">
              <Trans
                t={t}
                i18nKey="login.terms-checkbox"
                components={{
                  terms: (
                    <a
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                      className="font-semibold text-gray-900 underline"
                      href={`https://www.bestiebite.com/${
                        i18n.language === "it" ? "it" : "en"
                      }/terms`}
                      target="_blank"
                    />
                  ),
                  privacy: (
                    <a
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                      className="font-semibold text-gray-900 underline"
                      href={`https://www.bestiebite.com/${
                        i18n.language === "it" ? "it" : "en"
                      }/privacy`}
                      target="_blank"
                    />
                  ),
                }}
              />
            </span>
          </label>

          <label
            htmlFor="marketing"
            className="flex cursor-pointer items-start space-x-2"
          >
            <input
              type="checkbox"
              id="marketing"
              checked={marketingAccepted}
              onChange={() => {
                const newValue = !marketingAccepted;
                setMarketingAccepted(newValue);
                if (newValue) {
                  trackEvent("marketing_opted_in", { placeRef });
                }
              }}
              className="mt-0.5 size-4 cursor-pointer rounded border-gray-300 text-primary accent-primary focus:ring-2 focus:ring-primary"
            />
            <span className="flex-1 text-xs leading-relaxed">
              <Trans t={t} i18nKey="login.marketing-checkbox" />
            </span>
          </label>
        </div>
      </motion.div>
    </div>
  );
}
